'use client';

import { useCallback } from 'react';
import { ApiClientError, type LimitErrorCode } from '@/shared/lib/api';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { getActiveSubscriptionSnapshot } from '@/features/subscription/model/active-subscription.query';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';

/**
 * Optional side-effect callbacks fired when a typed limit error is routed to
 * the server-driven paywall. The paywall UI itself is shown automatically by
 * the root-mounted `<LimitPaywallDialog />` — these callbacks are only for
 * call-site-local cleanup (reset upload UI, warn on preflight drift, etc.).
 */
export interface LimitPaywallHandlers {
  onAngleLibraryLimit?: () => void;
  onConceptAngleLimit?: () => void;
  onProjectLimit?: () => void;
  onProjectReferenceLimit?: () => void;
  onProjectReferenceInsufficientTokens?: () => void;
  onInsufficientTokens?: () => void;
  onRenderQueueLimit?: () => void;
  onRenderDailyLimit?: () => void;
  onRenderWeeklyLimit?: () => void;
  onRenderMonthlyLimit?: () => void;
  onSubscriptionRequired?: () => void;
  onPackRequired?: () => void;
  onPackDesignsExhausted?: () => void;
  onPackAlbumsExhausted?: () => void;
  onPackEstimatesExhausted?: () => void;
  onFreeDesignsExhausted?: () => void;
}

const CODE_TO_HANDLER_KEY: Record<LimitErrorCode, keyof LimitPaywallHandlers> = {
  ANGLE_LIBRARY_LIMIT_EXCEEDED: 'onAngleLibraryLimit',
  CONCEPT_ANGLE_LIMIT_EXCEEDED: 'onConceptAngleLimit',
  PROJECT_LIMIT_EXCEEDED: 'onProjectLimit',
  PROJECT_REFERENCE_LIMIT_EXCEEDED: 'onProjectReferenceLimit',
  PROJECT_REFERENCE_INSUFFICIENT_TOKENS: 'onProjectReferenceInsufficientTokens',
  INSUFFICIENT_TOKENS: 'onInsufficientTokens',
  RENDER_QUEUE_LIMIT_EXCEEDED: 'onRenderQueueLimit',
  RENDER_DAILY_LIMIT_EXCEEDED: 'onRenderDailyLimit',
  RENDER_WEEKLY_LIMIT_EXCEEDED: 'onRenderWeeklyLimit',
  RENDER_MONTHLY_LIMIT_EXCEEDED: 'onRenderMonthlyLimit',
  // Advanced render controls (Hi-Res, Extended thinking, model, output style,
  // HIGH effort) and cost estimates now share ONE preflight chooser code
  // (PRO_FEATURE_UNAVAILABLE) opened on a locked-knob tap. On the 422 bypass
  // path the server returns PACK_REQUIRED, so there are no per-feature 422
  // codes to route here anymore.
  SUBSCRIPTION_REQUIRED: 'onSubscriptionRequired',
  // Facade Pack pivot. All are 422s carrying their paywall inline; the shared
  // dialog branches presentation off the payload's primary action kind
  // (purchase_pack → pack offer, open_pass_paywall target pro_plan →
  // Pro Plan sheet, null → info-only). The PACK_*_EXHAUSTED codes are
  // info-only (primary_action null): pack caps deplete in place with no
  // extension SKU to sell, so they render as an info notice, not a CTA.
  PACK_REQUIRED: 'onPackRequired',
  PACK_DESIGNS_EXHAUSTED: 'onPackDesignsExhausted',
  PACK_ALBUMS_EXHAUSTED: 'onPackAlbumsExhausted',
  PACK_ESTIMATES_EXHAUSTED: 'onPackEstimatesExhausted',
  FREE_DESIGNS_EXHAUSTED: 'onFreeDesignsExhausted',
};

/**
 * Non-hook router. Same logic as [useLimitErrorPaywall] but works outside the
 * React tree (e.g. from `MutationCache.onError`). Returns the matched code
 * when the error was routed to the paywall, or null otherwise so callers can
 * fire any code-specific side effects without re-doing the discriminator.
 */
export function routeLimitErrorToPaywall(error: unknown): LimitErrorCode | null {
  if (!(error instanceof ApiClientError)) return null;

  const code = error.code as LimitErrorCode | undefined;
  if (!code || !(code in CODE_TO_HANDLER_KEY)) return null;

  // 422 limits carry the paywall inline on the error body. SUBSCRIPTION_REQUIRED
  // is a 403 without an inline payload — we fall back to the preflight-cached
  // copy on the effective plan's paywall_payloads map. Other codes like
  // PROJECT_REFERENCE_LIMIT_EXCEEDED don't exist in that map because they
  // split by category (:image / :document) — those always come from the body.
  let payload = error.getPaywall();
  if (
    !payload &&
    (code === 'SUBSCRIPTION_REQUIRED' ||
      code === 'INSUFFICIENT_TOKENS' ||
      code === 'PACK_REQUIRED' ||
      code === 'FREE_DESIGNS_EXHAUSTED')
  ) {
    // `paywall_payloads` lives on the plan resource (real plan or guest plan),
    // mirroring `usePaywallPayloads` — `effectivePlan` only holds limits.
    // INSUFFICIENT_TOKENS / PACK_REQUIRED / FREE_DESIGNS_EXHAUSTED fall back
    // here only if a payload was missing on the error body (all current
    // responses carry it inline); the pack copy is static so the preflight
    // map is an exact mirror.
    const plan = getActiveSubscriptionSnapshot().plan;
    const attrs = plan && 'attributes' in plan ? plan.attributes : plan;
    payload = attrs?.paywall_payloads?.[code];
  }
  if (!payload) return null;

  // The error itself signals cache drift — server enforced a limit the
  // local snapshot didn't predict (e.g. admin grant just landed, balance
  // changed on another tab). Refetch the authoritative state so the next
  // click doesn't read the same stale cache and re-hit this path.
  queryClient.invalidateQueries({ queryKey: cacheKeys.balance });
  queryClient.invalidateQueries({ queryKey: cacheKeys.profile });
  queryClient.invalidateQueries({ queryKey: cacheKeys.subscriptions.active });

  // Meta rides along so the pack paywalls can bind to the building the limit
  // fired on (meta.project_id) instead of guessing from the current route.
  useLimitPaywallStore.getState().showPaywall(payload, code, error.meta);
  return code;
}

/**
 * Central router for typed limit errors. When an error has a server-driven
 * paywall payload, the shared dialog is shown and any matching side-effect
 * callback is invoked. Returns true when handled (caller can skip its
 * generic error branch).
 */
export function useLimitErrorPaywall(
  handlers: LimitPaywallHandlers = {}
): (error: unknown) => boolean {
  return useCallback(
    (error: unknown): boolean => {
      const code = routeLimitErrorToPaywall(error);
      if (!code) return false;
      handlers[CODE_TO_HANDLER_KEY[code]]?.();
      return true;
    },
    [handlers]
  );
}
